import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import "./main.scss";
import { agmsClientRequestGet, agmsClientRequestPut } from "../../apis/request";
import Loading from "../../assets/uxnicon/loadingIcon.png";
import AdminLayout from "./AdminLayout";

const PAGE_SIZE = 20;

const thBase = {
  backgroundColor: "#787397",
  color: "#fff",
  fontWeight: "bold",
  fontFamily: "Noto Sans CJK KR",
  padding: "10px 16px",
  borderRight: "1px solid #EBEBEB",
  textAlign: "center",
};

const tdBase = {
  backgroundColor: "#fff",
  textAlign: "center",
  borderRight: "1px solid #EBEBEB",
  borderBottom: "1px solid #EBEBEB",
  padding: "8px 12px",
};

const pageBtn = (active, disabled) => ({
  minWidth: "34px",
  padding: "6px 10px",
  border: active ? "1px solid #787397" : "1px solid #d5d0dd",
  borderRadius: "6px",
  background: active ? "#787397" : "#fff",
  color: active ? "#fff" : "#5E2589",
  fontWeight: active ? "bold" : "normal",
  cursor: disabled ? "default" : "pointer",
  opacity: disabled ? 0.4 : 1,
});

const restoreBtn = {
  padding: "5px 14px",
  border: "1px solid #4a8f5b",
  background: "#fff",
  color: "#2f7a41",
  borderRadius: "6px",
  fontSize: "13px",
  cursor: "pointer",
};

const modalOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const modalBox = {
  background: "#fff",
  borderRadius: "12px",
  padding: "28px",
  width: "360px",
  maxWidth: "92vw",
  boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
};

const AdminWithdrawnList = () => {
  const [pageResp, setPageResp] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(null);

  const callApi = async (p, s) => {
    setError(null);
    try {
      const res = await agmsClientRequestGet(`/api/admin/withdrawn?page=${p}&size=${PAGE_SIZE}&search=${encodeURIComponent(s || "")}`);
      if (res && Array.isArray(res.content)) {
        setPageResp(res);
      } else {
        setPageResp({ content: [], totalElements: 0, totalPages: 1, page: p, size: PAGE_SIZE });
      }
    } catch (e) {
      setError("탈퇴 목록을 불러오지 못했습니다. (권한 또는 서버 오류)");
      setPageResp({ content: [], totalElements: 0, totalPages: 1, page: p, size: PAGE_SIZE });
    }
  };

  useEffect(() => {
    callApi(page, search);
  }, [page, search]);

  const applySearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const confirmRestore = async () => {
    if (!restoreTarget) return;
    setBusy(true);
    try {
      await agmsClientRequestPut(`/api/admin/users/${restoreTarget.id}/restore`);
      setRestoreTarget(null);
      const remaining = (pageResp?.content?.length ?? 1) - 1;
      const nextPage = remaining === 0 && page > 1 ? page - 1 : page;
      if (nextPage !== page) {
        setPage(nextPage);
      } else {
        await callApi(page, search);
      }
    } catch (e) {
      alert("복구에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  if (pageResp === null) {
    return (
      <AdminLayout>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "300px" }}>
          <img
            src={Loading}
            alt="loading..."
            style={{ width: "50px", height: "50px", animation: ".75s linear infinite spinner-border" }}
          />
        </div>
      </AdminLayout>
    );
  }

  const content = pageResp.content ?? [];
  const total = pageResp.totalElements ?? 0;
  const totalPages = Math.max(1, pageResp.totalPages ?? 1);
  const currentPage = Math.min(page, totalPages);

  return (
    <AdminLayout>
      <div style={{ width: "100%" }}>
        <div style={{ marginBottom: "17px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: "bold", fontSize: "20px" }}>탈퇴 목록</div>
          <div style={{ color: "#84818A" }}>총 {total}명</div>
        </div>

        <div style={{ color: "#84818A", fontSize: "13px", marginBottom: "14px" }}>
          본인이 탈퇴한 유저입니다. 데이터는 보존되어 있으며, 복구하면 다시 로그인할 수 있게 됩니다.
        </div>

        {/* 검색 */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "14px" }}>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }}
            placeholder="이름 또는 이메일 검색"
            style={{ flex: "0 0 320px", maxWidth: "100%", padding: "8px 12px", border: "1px solid #CBCBCB", borderRadius: "8px" }}
          />
          <button
            onClick={applySearch}
            style={{ padding: "8px 16px", background: "#787397", color: "#fff", border: "none", borderRadius: "8px", fontWeight: "bold", cursor: "pointer" }}
          >
            검색
          </button>
          {search && (
            <button
              onClick={() => { setSearchInput(""); setSearch(""); setPage(1); }}
              style={{ padding: "8px 14px", background: "#fff", color: "#5E2589", border: "1px solid #d5d0dd", borderRadius: "8px", cursor: "pointer" }}
            >
              초기화
            </button>
          )}
        </div>

        {error && <div style={{ color: "#d9534f", marginBottom: "12px" }}>{error}</div>}

        <div style={{ overflowX: "auto" }}>
          <table style={{ minWidth: "100%" }}>
            <thead>
              <tr>
                <th style={{ ...thBase, borderRadius: "5px 0px 0px 5px", minWidth: "60px" }}>ID</th>
                <th style={{ ...thBase, minWidth: "90px" }}>이름</th>
                <th style={{ ...thBase, minWidth: "220px" }}>이메일</th>
                <th style={{ ...thBase, minWidth: "80px" }}>권한</th>
                <th style={{ ...thBase, minWidth: "60px" }}>성별</th>
                <th style={{ ...thBase, minWidth: "60px" }}>나이</th>
                <th style={{ ...thBase, minWidth: "80px" }}>당뇨유형</th>
                <th style={{ ...thBase, borderRight: "none", borderRadius: "0px 5px 5px 0px", minWidth: "100px" }}>복구</th>
              </tr>
            </thead>
            <tbody>
              {content.length === 0 ? (
                <tr>
                  <td style={{ ...tdBase, borderRight: "none" }} colSpan={8}>
                    탈퇴한 유저가 없습니다.
                  </td>
                </tr>
              ) : (
                content.map((u, i) => {
                  const cellBg = hoverIdx === i ? "#EFE9F7" : "#fff";
                  const cell = { ...tdBase, backgroundColor: cellBg };
                  const cellLast = { ...tdBase, backgroundColor: cellBg, borderRight: "none" };
                  return (
                  <tr key={u.id ?? i} onMouseEnter={() => setHoverIdx(i)} onMouseLeave={() => setHoverIdx(null)}>
                    <td style={cell}>{u.id}</td>
                    <td style={cell}>{u.name}</td>
                    <td style={cell}>{u.email}</td>
                    <td style={cell}>{u.role_name}</td>
                    <td style={cell}>{u.sex_name ?? "-"}</td>
                    <td style={cell}>{u.age ?? "-"}</td>
                    <td style={cell}>{u.diabetes_type_name ?? "-"}</td>
                    <td style={cellLast}>
                      <button style={restoreBtn} onClick={() => setRestoreTarget(u)}>복구</button>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {content.length > 0 && (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "6px", marginTop: "20px" }}>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} style={pageBtn(false, currentPage === 1)}>
              이전
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p} onClick={() => setPage(p)} style={pageBtn(p === currentPage, false)}>
                {p}
              </button>
            ))}
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={pageBtn(false, currentPage === totalPages)}>
              다음
            </button>
          </div>
        )}
      </div>

      {/* 복구 확인 모달 */}
      {restoreTarget && (
        <div style={modalOverlay} onClick={() => !busy && setRestoreTarget(null)}>
          <div style={modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: "bold", fontSize: "18px", marginBottom: "12px" }}>유저 복구</div>
            <div style={{ color: "#555", lineHeight: 1.6, marginBottom: "20px" }}>
              <b>{restoreTarget.name}</b> ({restoreTarget.email}) 유저를 복구할까요?<br />
              전체 유저 목록으로 돌아가고 로그인이 다시 허용됩니다.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button
                onClick={() => setRestoreTarget(null)}
                disabled={busy}
                style={{ padding: "9px 18px", background: "#fff", color: "#5E2589", border: "1px solid #d5d0dd", borderRadius: "8px", cursor: "pointer" }}
              >
                취소
              </button>
              <button
                onClick={confirmRestore}
                disabled={busy}
                style={{ padding: "9px 18px", background: "#2f7a41", color: "#fff", border: "none", borderRadius: "8px", fontWeight: "bold", cursor: "pointer" }}
              >
                {busy ? "복구 중..." : "복구"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default withRouter(AdminWithdrawnList);
